// SPDX-License-Identifier: GPL-3.0-only
// OpenSaga GizForces_Update/Reset/Activate/GetOutput and GizBuildIts_Update,
// GizDrawBuildItPiece, GizBuildItMul, GizBuildIt_FindNearest. Original ANI5
// matrices come from scene-animation.mjs; no procedural substitute geometry.
import {Matrix4, Quaternion} from 'three';

function vector(value) {
  if (value && !Array.isArray(value) && !ArrayBuffer.isView(value)) value = [value.x, value.y, value.z];
  if (!value || value.length !== 3 || !Array.from(value).every(Number.isFinite)) throw new Error('Invalid interaction position.');
  return Array.from(value);
}
const position = matrix => matrix.slice(12, 15);
const add = (a, b) => a.map((v, i) => v + b[i]);
const sinHalfTurn = ratio => Math.sin((Math.trunc(ratio * 32768) >> 1) * (2 * Math.PI / 32768));

export function interpolateBuildPiece(start, end, progress) {
  if (![start, end].every(m => m?.length === 16 && Array.from(m).every(Number.isFinite)) || !Number.isFinite(progress))
    throw new Error('Invalid original build piece transform.');
  const t = Math.max(0, Math.min(1, progress));
  if (t === 0) return Array.from(start);
  if (t === 1) return Array.from(end);
  // Source removes translation, converts the two authored matrices to
  // quaternions, slerps rotation, linearly moves XYZ and adds a 0.1-unit hop.
  const a = new Matrix4().fromArray(start), b = new Matrix4().fromArray(end);
  a.elements[12] = a.elements[13] = a.elements[14] = 0;
  b.elements[12] = b.elements[13] = b.elements[14] = 0;
  const rotation = new Quaternion().setFromRotationMatrix(a).slerp(new Quaternion().setFromRotationMatrix(b), t);
  const result = new Matrix4().makeRotationFromQuaternion(rotation).toArray();
  for (let axis = 0; axis < 3; axis++) result[12 + axis] = start[12 + axis] + (end[12 + axis] - start[12 + axis]) * t;
  result[13] += 0.1 * sinHalfTurn(t);
  return result;
}

export function buildStepMultiplier({fastBuild = false, buildButtonTaps = 0} = {}, completed, total) {
  if (fastBuild) return 3;
  let amount = Math.trunc(buildButtonTaps);
  if (!Number.isFinite(amount)) throw new Error('Invalid build tap count.');
  if (Math.trunc((completed - amount + total) / 2) <= completed) amount = total - completed;
  return Math.max(0, Math.min(10, amount)) / 10 + 1;
}

export function createInteractionState(gizmos, {animationAdapter, progress = {}, gizmoFlags = {}, lineOfSight = null} = {}) {
  if (!gizmos || !animationAdapter || typeof animationAdapter.resolve !== 'function') throw new Error('Original interaction animation adapter required.');
  const records = [], byId = new Map(), byName = new Map(), events = [];
  const lookup = (container, key) => container instanceof Map ? container.get(key) : container?.[key];
  function resolve(ref) {
    if (typeof ref === 'string') return byId.get(ref) ?? byName.get(ref.toLowerCase()) ?? null;
    if (!ref) return null;
    return byName.get(`${String(ref.type).toLowerCase()}:${String(ref.name).toLowerCase()}`) ?? null;
  }
  function emit(type, r, extra = {}) {events.push({type, id: r.id, name: r.name, kind: r.kind, ...extra});}
  function setMatrix(piece, matrix) {
    piece.matrix = matrix;
    if (typeof animationAdapter.setMatrix === 'function') animationAdapter.setMatrix(piece.name, matrix);
  }
  function sample(piece, frame) {
    piece.frame = frame; const matrix = piece.binding.sample(frame); setMatrix(piece, matrix); return matrix;
  }
  function visibility(r, visible) {
    r.visible = !!visible;
    for (const piece of r.pieces) animationAdapter.setVisible?.(piece.name, r.visible);
  }
  // EvaluateState ignores static objects and gives AtEnd precedence when all
  // animated objects are at both endpoints (including an empty animation set).
  function atEnd(r) {return r.pieces.filter(p => p.binding.animationIndex !== undefined).every(p => p.frame === p.endFrame);}
  function atStart(r) {return !atEnd(r) && r.pieces.filter(p => p.binding.animationIndex !== undefined).every(p => p.frame === p.startFrame);}
  function complete(r) {
    if (r.kind === 'build') return r.buildState === 2;
    if (r.configFlags & 2) return false;
    if (r.forceRange > 0 && !r.forceRangeComplete) return false;
    if ((r.configFlags & 64) && !r.alongSocketHidden) return false;
    return r.animationReversed ? atStart(r) : atEnd(r);
  }
  function stepTime(r, actor = null) {r.stepDuration = 0.3 / (actor ? buildStepMultiplier(actor, r.builtCount, r.pieces.length) : 1); r.stepTimer = r.stepDuration;}
  function resetPieces(r) {
    r.actor = null; r.builtCount = 0; r.buildState = 0; r.forceRangeTimer = 0;
    r.forceRangeComplete = false; r.completionReleased = false; r.destroyed = false; r.animationReversed = false;
    for (const piece of r.pieces) sample(piece, piece.startFrame);
    if (r.kind === 'build') stepTime(r);
  }
  for (const [kind, authored] of [['force', gizmos.forces ?? []], ['build', gizmos.buildits ?? []]]) for (const source of authored) {
    const saved = lookup(progress, source.id) ?? {}, flags = lookup(gizmoFlags, source.id) ?? lookup(gizmoFlags, source.name.toLowerCase()) ?? {};
    const r = {...source, source, kind, enabled: saved.enabled ?? flags.enabled ?? true, visible: saved.visible ?? flags.visible ?? true,
      pieces: [], unsupported: [], actor: null, rewardReleased: saved.rewardReleased ?? false, completionReleased: false,
      forceRangeComplete: false, forceRangeTimer: 0, alongSocketHidden: false, animationReversed: false, reverseActive: false,
      destroyed: false, builtCount: 0, buildState: 0, position: vector(source.position)};
    if (kind === 'force' && source.groupIndex >= 0) r.unsupported.push('stack group runtime');
    if (kind === 'force' && (source.configFlags & 64)) r.unsupported.push('along-socket runtime');
    if (kind === 'build' && source.linkedBuildit) r.unsupported.push('linked-build transform');
    if (kind === 'build' && (source.configFlags & 64)) r.unsupported.push('automatic-build callback');
    for (const object of source.animationSet?.objects ?? []) {
      if (!object.name) continue;
      const binding = animationAdapter.resolve(object.name);
      if (!binding || binding.unsupported) {r.unsupported.push(`missing original animation: ${object.name}`); continue;}
      const startFrame = Number(object.startFrame), endFrame = Number(object.endFrame);
      if (![startFrame, endFrame].every(Number.isFinite)) throw new Error('Invalid interaction frame range.');
      r.pieces.push({...object, binding, startFrame, endFrame, frame: startFrame,
        startMatrix: binding.sample(startFrame), endMatrix: binding.sample(endFrame)});
    }
    resetPieces(r);
    if (kind === 'build' && r.pieces.length) {
      const starts = r.pieces.map(p => position(p.startMatrix)), min = starts[0].slice(), max = starts[0].slice();
      for (const point of starts) for (let axis = 0; axis < 3; axis++) {min[axis] = Math.min(min[axis], point[axis]); max[axis] = Math.max(max[axis], point[axis]);}
      r.startPosition = min.map((v, i) => (v + max[i]) / 2);
      r.boundsRadius = Math.max(max[0] - min[0], max[2] - min[2]) * Math.SQRT1_2;
    }
    if (saved.complete) {
      for (const piece of r.pieces) sample(piece, piece.endFrame);
      if (kind === 'build') {r.buildState = 2; r.builtCount = r.pieces.length;}
      r.forceRangeComplete = true; r.completionReleased = true;
    }
    visibility(r, r.visible);
    records.push(r); byId.set(r.id, r); byName.set(r.name.toLowerCase(), r);
    byName.set(`${kind === 'force' ? 'gizforce' : 'gizbuildit'}:${r.name.toLowerCase()}`, r);
  }
  function canUse(ref, actor) {
    const r = resolve(ref);
    if (!r || !actor || !r.enabled || !r.visible || r.unsupported.length || !r.pieces.length || r.destroyed || complete(r)) return false;
    const p = vector(actor.collisionPosition ?? actor.position);
    if (r.kind === 'force') {
      if (actor.canForce !== true || ((r.configFlags & 16) && actor.darkForce !== true)) return false;
      const delta = r.position.map((v, i) => v - p[i]);
      if (delta.reduce((sum, v) => sum + v * v, 0) > r.interactionRadius ** 2) return false;
      if (actor.facing) {const facing = vector(actor.facing); if (delta[0] * facing[0] + delta[2] * facing[2] < 0) return false;}
      if (actor.lineOfSight === false || (lineOfSight && !lineOfSight(r, actor))) return false;
      return true;
    }
    if (actor.canBuild !== true || r.buildState !== 0 || ((r.configFlags & 128) && actor.shadowMode === 0)) return false;
    const height = r.authoredProgress / 100 * r.boundsRadius, center = r.startPosition;
    const bottom = actor.lowerY ?? p[1], top = actor.upperY ?? p[1];
    if (top < center[1] - height || center[1] + height < bottom) return false;
    return (p[0] - center[0]) ** 2 + (p[2] - center[2]) ** 2 < (r.boundsRadius + 0.3) ** 2;
  }
  function reward(r) {
    if (r.rewardReleased) return;
    r.rewardReleased = true;
    const amount = r.kind === 'force' ? r.pickupCount : r.unknown5e;
    if (amount) emit('reward', r, {amount, position: add(r.position, r.effectPosition),
      rotationX: r.kind === 'force' ? r.pickupRotationX : r.unknown60,
      rotationY: r.kind === 'force' ? r.pickupRotationY : r.unknown62, activationRadius: r.activationRadius});
  }
  function update(dt) {
    if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Interaction dt must be in [0,1].');
    for (const r of records) {
      if ((!r.enabled && !r.commandDirection) || !r.visible || r.unsupported.length || !r.pieces.length) continue;
      if (r.kind === 'force') {
        const forward = r.commandDirection ? r.commandDirection > 0 : !!(r.actor || r.pendingCompletion);
        if (!atEnd(r) || forward || r.commandDirection) for (const piece of r.pieces) {
          const direction = piece.endFrame >= piece.startFrame ? 1 : -1;
          const sign = forward !== r.animationReversed ? 1 : -1;
          const speed = (r.actor?.fastForce ? 3 : 1) * (forward ? r.startFrame : r.endFrame);
          const next = piece.frame + dt * 60 * piece.binding.timeFactor * speed * direction * sign;
          sample(piece, Math.max(Math.min(piece.startFrame, piece.endFrame), Math.min(Math.max(piece.startFrame, piece.endFrame), next)));
        }
        if (forward && atEnd(r) && r.forceRange > 0 && !r.forceRangeComplete) {
          r.forceRangeTimer += dt;
          if (r.forceRangeTimer >= r.forceRange) {r.forceRangeComplete = true; r.forceRangeTimer = 0;}
        } else if (!forward) r.forceRangeTimer = 0;
        if (complete(r) && !r.completionReleased && forward) {
          r.completionReleased = true; reward(r); emit('complete', r);
          if (r.blowupName) emit('blowup', r, {blowupName: r.blowupName, position: r.position.slice()});
        }
      } else if (r.buildState === 1) {
        r.stepTimer += dt;
        const t = Math.min(1, r.stepTimer / 0.6);
        for (const piece of r.pieces) {
          const matrix = piece.endMatrix.slice(), offset = 0.2 * sinHalfTurn(t);
          if (r.unknown5c) {
            const angle = r.unknown5c * Math.PI / 180; matrix[12] += Math.sin(angle) * offset; matrix[14] += Math.cos(angle) * offset;
          } else matrix[13] += offset;
          setMatrix(piece, matrix);
        }
        if (r.stepTimer >= 0.6) {
          r.buildState = 2; r.actor = null; reward(r); emit('complete', r);
          if (r.configFlags & 2) visibility(r, false);
          if (r.blowupName) emit('blowup', r, {blowupName: r.blowupName, position: r.position.slice()});
        }
      } else if (r.buildState === 0) {
        if (!r.actor) {stepTime(r); continue;}
        r.stepTimer -= dt;
        const piece = r.pieces[r.builtCount];
        if (r.stepTimer <= 0) {
          sample(piece, piece.endFrame); emit('piecePlaced', r, {objectName: piece.name, pieceIndex: r.builtCount});
          r.builtCount++;
          if (r.builtCount === r.pieces.length) {r.buildState = 1; r.stepTimer = 0; r.actor = null; emit('finishing', r);}
          else stepTime(r, r.actor);
        } else setMatrix(piece, interpolateBuildPiece(piece.startMatrix, piece.endMatrix, 1 - r.stepTimer / r.stepDuration));
      }
    }
  }
  return {records, byId, resolve, canUse, update,
    use(ref, actor) {const r = resolve(ref); if (!canUse(r?.id, actor)) return false; if (!r.actor) emit('started', r); r.actor = actor; return true;},
    release(ref) {const r = resolve(ref); if (!r) return false; r.actor = null; return true;},
    activate(ref, on) {
      const r = resolve(ref); if (!r) return false;
      const wasEnabled = r.enabled; r.enabled = !!on; if (!on) {r.actor = null; return true;}
      if (r.kind === 'build' || !wasEnabled) resetPieces(r);
      r.commandDirection = 0; r.reverseActive = false;
      if ((r.kind === 'build' && (r.configFlags & 512)) || (r.kind === 'force' && (r.configFlags & 8))) r.rewardReleased = false;
      if (r.kind === 'build') visibility(r, true);
      return true;
    },
    activateReverse(ref, on, flags = 0) {
      const r = resolve(ref); if (!r || r.kind !== 'force') return false;
      // GizmoForce_ActivateRev's query branch checks only bit0. Generic
      // GizmoActivateReverse uses queries3/7, followed by commands2/6.
      if (flags & 1) return !!on !== r.reverseActive;
      r.reverseActive = !!on; r.commandDirection = on ? -1 : 1; r.enabled = !on; r.actor = null; return true;
    },
    setVisibility(ref, on) {const r = resolve(ref); if (!r) return false; visibility(r, on); return true;},
    getOutput(ref, output, ignoreActivation = false) {
      const r = resolve(ref); if (!r || r.unsupported.length) return null;
      if (!ignoreActivation && (!r.enabled || !r.visible)) return false;
      if (r.kind === 'build') return output === 0 ? r.buildState === 2 : null;
      if (output === 0) return atEnd(r); if (output === 1) return !atStart(r); if (output === 2) return atStart(r);
      if (output === 5) return r.destroyed; if (output === 6) return complete(r); if (output === 7) return !!r.actor;
      return null;
    },
    getDrawStates() {return records.flatMap(r => r.pieces.map(p => ({id: r.id, name: p.name, matrix: p.matrix.slice(), visible: r.visible,
      frame: p.frame, kind: r.kind})));},
    drainEvents() {return events.splice(0);},
    limitations: ['stack groups', 'along-socket forces', 'linked/automatic builds', 'idle randomized wobble',
      'actor hand-part effects', 'blowup/debris/reward payload rendering supplied by event consumer']
  };
}
